import { z } from "zod";

import { jurisdictionProfileSchema } from "./cableContract";
import { calculationRunSummarySchema } from "./calculationRun";
import { exactDecimalSchema } from "./faultContract";
import {
  CHOOSE_A_FAULT_RUN,
  type FaultRunChoice,
  listFaultRunsForRevision,
} from "./faultLink";
import { ApiError, describeApiFieldError } from "./http";
import {
  ALWAYS_STUDY_INFORMATION_CODES,
  backupDeviceKindSchema,
  connectionModeSchema,
  continuousVoltageBasisSchema,
  coordinationDeclarationSchema,
  earthingSystemSchema,
  faultTypeSchema,
  locationKindSchema,
  lpsPresenceSchema,
  prospectiveCurrentBasisSchema,
  prospectiveCurrentSourceSchema,
  protectionLevelBasisSchema,
  shortCircuitCaseSchema,
  spdTypeSchema,
  surgeProtectionStatusSchema,
  surgeProtectionWarningCodeSchema,
  upBasisSchema,
} from "./surgeProtectionContract";

const optionalRequestText = (maxLength: number) =>
  z.string().trim().max(maxLength).nullable().optional();

const optionalSource = (maxLength = 300) => z.string().trim().min(1).max(maxLength).optional();

const RUNS_URL = "/api/v1/electrical/surge-protection/runs";

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// The fault-run selector (list and sentence) is the one the protection studies share; an SPD
// study names a stored EOS-04 run of the revision in the same way (design note section 2).
export { CHOOSE_A_FAULT_RUN, listFaultRunsForRevision };
export type { FaultRunChoice };

// The choices the study and the SPD need; the backend imposes no default on them.
export const REQUIRED_STUDY_CHOICES = ["location_kind", "earthing_system", "connection_mode"] as const;
export const REQUIRED_SPD_CHOICES = ["spd_type"] as const;

/** One declared figure with its source; the unit is fixed by the field that holds it. */
export const declaredRequestSchema = z
  .object({ value: exactDecimalSchema, source: optionalSource() })
  .strict();

// The connecting leads: the voltage drop declared, or the length with its inductive drop.
export const leadsRequestSchema = z
  .object({
    lead_voltage_kv: declaredRequestSchema.optional(),
    lead_length_m: declaredRequestSchema.optional(),
    lead_inductive_drop_kv_per_m: declaredRequestSchema.optional(),
  })
  .strict()
  .superRefine((leads, ctx) => {
    if ((leads.lead_length_m === undefined) !== (leads.lead_inductive_drop_kv_per_m === undefined)) {
      ctx.addIssue({
        code: "custom",
        message: "declare the lead length and the inductive drop per metre together",
        path: ["lead_inductive_drop_kv_per_m"],
      });
    }
    if (leads.lead_voltage_kv === undefined && leads.lead_length_m === undefined) {
      ctx.addIssue({
        code: "custom",
        message: "declare the lead voltage, or the lead length with its inductive drop",
        path: ["lead_voltage_kv"],
      });
    }
  });

export const backupDeviceRequestSchema = z
  .object({
    kind: backupDeviceKindSchema.optional(),
    rating_a: exactDecimalSchema,
    source: optionalSource(),
    sld_device_code: z.string().trim().min(1).max(50).optional(),
  })
  .strict()
  .superRefine((device, ctx) => {
    if (device.kind === undefined) {
      ctx.addIssue({
        code: "invalid_type",
        expected: "string",
        received: "undefined",
        message: "kind is required",
        path: ["kind"],
      });
    }
  });

// The SPD's declared parameters by its type: Iimp on Type 1 and 1 + 2, In on Type 2 and 1 + 2.
export const spdRequestSchema = z
  .object({
    spd_type: spdTypeSchema.optional(),
    up_kv: declaredRequestSchema,
    up_basis: upBasisSchema.optional(),
    uc_v: declaredRequestSchema,
    ut_v: declaredRequestSchema.optional(),
    iimp_ka: declaredRequestSchema.optional(),
    in_ka: declaredRequestSchema.optional(),
    imax_ka: declaredRequestSchema.optional(),
    iimp_per_mode_declared: z.boolean().optional(),
    isccr_ka: declaredRequestSchema.optional(),
    backup_device: backupDeviceRequestSchema.optional(),
    protection_distance_m: declaredRequestSchema.optional(),
    coordination: coordinationDeclarationSchema.optional(),
    coordination_source: optionalSource(),
    coordinated_spd_reference: z.string().trim().min(1).max(200).optional(),
    manufacturer_reference: z.string().trim().min(1).max(200).optional(),
  })
  .strict()
  .superRefine((spd, ctx) => {
    if (spd.spd_type === undefined) {
      ctx.addIssue({
        code: "invalid_type",
        expected: "string",
        received: "undefined",
        message: "spd_type is required",
        path: ["spd_type"],
      });
      return;
    }
    const carriesIimp = spd.spd_type !== "TYPE_2";
    const carriesIn = spd.spd_type !== "TYPE_1";
    if (carriesIimp && spd.iimp_ka === undefined) {
      ctx.addIssue({
        code: "custom",
        message: "a Type 1 or Type 1 + 2 SPD declares its impulse current Iimp",
        path: ["iimp_ka"],
      });
    }
    if (!carriesIimp && spd.iimp_ka !== undefined) {
      ctx.addIssue({
        code: "custom",
        message: "a Type 2 SPD carries no impulse current Iimp",
        path: ["iimp_ka"],
      });
    }
    if (carriesIn && spd.in_ka === undefined) {
      ctx.addIssue({
        code: "custom",
        message: "a Type 2 or Type 1 + 2 SPD declares its nominal discharge current In",
        path: ["in_ka"],
      });
    }
    if (spd.imax_ka !== undefined && spd.in_ka === undefined) {
      ctx.addIssue({ code: "custom", message: "Imax is declared beside In", path: ["imax_ka"] });
    }
    if (
      (spd.coordination ?? "NOT_DECLARED") === "NOT_DECLARED" &&
      (spd.coordination_source !== undefined || spd.coordinated_spd_reference !== undefined)
    ) {
      ctx.addIssue({
        code: "custom",
        message: "a coordination that is not declared names no source or coordinated product",
        path: ["coordination"],
      });
    }
  });

export const requirementsRequestSchema = z
  .object({
    uc_minimum_v: declaredRequestSchema.optional(),
    ut_required_v: declaredRequestSchema.optional(),
    iimp_required_ka: declaredRequestSchema.optional(),
    in_required_ka: declaredRequestSchema.optional(),
    imax_required_ka: declaredRequestSchema.optional(),
  })
  .strict();

export const protectedItemRequestSchema = z
  .object({
    code: z.string().trim().min(1).max(50),
    name: z.string().trim().min(1).max(200),
    uw_kv: declaredRequestSchema,
    distance_m: exactDecimalSchema.optional(),
    notes: optionalRequestText(500),
  })
  .strict();

// One SPD location with 1..n protected items; the prospective current from exactly one place
// (a linked fault run or the declared figure) or none - NOT ESTABLISHED, never assumed.
export const surgeProtectionRequestSchema = z
  .object({
    code: z.string().trim().min(1).max(50),
    name: z.string().trim().min(1).max(200),
    spd_code: z.string().trim().min(1).max(50),
    location_kind: locationKindSchema.optional(),
    system_voltage_v: declaredRequestSchema,
    earthing_system: earthingSystemSchema.optional(),
    connection_mode: connectionModeSchema.optional(),
    connection_mode_source: optionalSource(),
    sld_bus_code: z.string().trim().min(1).max(50).optional(),
    fault_run_id: z.string().optional(),
    prospective_current: declaredRequestSchema.optional(),
    lps_presence: lpsPresenceSchema.optional(),
    spd: spdRequestSchema,
    leads: leadsRequestSchema.optional(),
    requirements: requirementsRequestSchema.optional(),
    items: z.array(protectedItemRequestSchema).min(1).max(200),
    upstream_spd_code: z.string().trim().min(1).max(50).optional(),
    downstream_spd_code: z.string().trim().min(1).max(50).optional(),
    jurisdiction_profile: jurisdictionProfileSchema.optional(),
    notes: optionalRequestText(1000),
  })
  .strict()
  .superRefine((values, ctx) => {
    for (const field of REQUIRED_STUDY_CHOICES) {
      if (values[field] === undefined) {
        ctx.addIssue({
          code: "invalid_type",
          expected: "string",
          received: "undefined",
          message: `${field} is required`,
          path: [field],
        });
      }
    }
    if (values.fault_run_id !== undefined && values.prospective_current !== undefined) {
      ctx.addIssue({
        code: "custom",
        message: "choose one prospective current: a linked fault run, or the declared figure",
        path: ["fault_run_id"],
      });
    } else if (values.fault_run_id !== undefined && !UUID.test(values.fault_run_id)) {
      ctx.addIssue({ code: "custom", message: CHOOSE_A_FAULT_RUN, path: ["fault_run_id"] });
    }
    const codes = values.items.map((item) => item.code);
    if (new Set(codes).size !== codes.length) {
      ctx.addIssue({
        code: "custom",
        message: "protected item codes must be unique within a study",
        path: ["items"],
      });
    }
  });

export const surgeProtectionWarningSchema = z
  .object({ code: surgeProtectionWarningCodeSchema, message: z.string() })
  .strict();

export const declaredResponseSchema = z
  .object({ value: z.string(), source: z.string().nullable() })
  .strict();

export const linkedFaultRunResponseSchema = z
  .object({
    fault_run_id: z.string().uuid(),
    study_code: z.string(),
    revision_number: z.number().int(),
    fault_bus_code: z.string(),
    calculation_case: shortCircuitCaseSchema,
    fault_type: faultTypeSchema,
    nominal_voltage_v: z.string(),
  })
  .strict();

export const prospectiveCurrentResponseSchema = z
  .object({
    value_ka: z.string(),
    source: prospectiveCurrentSourceSchema,
    source_text: z.string().nullable(),
    linked_fault_run: linkedFaultRunResponseSchema.nullable(),
  })
  .strict();

export const leadsResponseSchema = z
  .object({
    lead_voltage_kv: declaredResponseSchema.nullable(),
    lead_length_m: declaredResponseSchema.nullable(),
    lead_inductive_drop_kv_per_m: declaredResponseSchema.nullable(),
  })
  .strict();

export const backupDeviceResponseSchema = z
  .object({
    kind: backupDeviceKindSchema,
    rating_a: z.string(),
    source: z.string().nullable(),
    sld_device_code: z.string().nullable(),
  })
  .strict();

export const spdResponseSchema = z
  .object({
    spd_type: spdTypeSchema,
    up_kv: declaredResponseSchema,
    uc_v: declaredResponseSchema,
    up_basis: upBasisSchema,
    ut_v: declaredResponseSchema.nullable(),
    iimp_ka: declaredResponseSchema.nullable(),
    in_ka: declaredResponseSchema.nullable(),
    imax_ka: declaredResponseSchema.nullable(),
    iimp_per_mode_declared: z.boolean(),
    isccr_ka: declaredResponseSchema.nullable(),
    backup_device: backupDeviceResponseSchema.nullable(),
    protection_distance_m: declaredResponseSchema.nullable(),
    coordination: coordinationDeclarationSchema,
    coordination_source: z.string().nullable(),
    coordinated_spd_reference: z.string().nullable(),
    manufacturer_reference: z.string().nullable(),
  })
  .strict();

export const requirementsResponseSchema = z
  .object({
    uc_minimum_v: declaredResponseSchema.nullable(),
    ut_required_v: declaredResponseSchema.nullable(),
    iimp_required_ka: declaredResponseSchema.nullable(),
    in_required_ka: declaredResponseSchema.nullable(),
    imax_required_ka: declaredResponseSchema.nullable(),
  })
  .strict();

// One protected item as checked: its comparisons formed exactly when their inputs exist
// (null otherwise; F1: a field added later is optional on this side).
export const protectedItemResponseSchema = z
  .object({
    code: z.string(),
    name: z.string(),
    uw_kv: z.string(),
    uw_source: z.string().nullable(),
    distance_m: z.string().nullable(),
    margin_kv: z.string().nullable(),
    ratio: z.string().nullable(),
    met: z.boolean().nullable(),
    distance_excess_m: z.string().nullable(),
    within_protection_distance: z.boolean().nullable(),
    status: surgeProtectionStatusSchema,
    warnings: z.array(surgeProtectionWarningSchema),
    notes: z.string().nullable(),
  })
  .strict();

export const surgeProtectionResponseSchema = z
  .object({
    code: z.string(),
    name: z.string(),
    spd_code: z.string(),
    location_kind: locationKindSchema,
    system_voltage_v: declaredResponseSchema,
    earthing_system: earthingSystemSchema,
    connection_mode: connectionModeSchema,
    connection_mode_source: z.string().nullable(),
    sld_bus_code: z.string().nullable(),
    lps_presence: lpsPresenceSchema,
    spd: spdResponseSchema,
    leads: leadsResponseSchema.nullable(),
    requirements: requirementsResponseSchema,
    prospective_current: prospectiveCurrentResponseSchema.nullable(),
    upstream_spd_code: z.string().nullable(),
    downstream_spd_code: z.string().nullable(),
    protection_level_basis: protectionLevelBasisSchema,
    du_leads_kv: z.string().nullable(),
    up_eff_kv: z.string().nullable(),
    lowest_uw_kv: z.string(),
    protection_level_margin_kv: z.string().nullable(),
    protection_level_met: z.boolean().nullable(),
    continuous_voltage_basis: continuousVoltageBasisSchema,
    uc_margin_v: z.string().nullable(),
    uc_ratio: z.string().nullable(),
    uc_met: z.boolean().nullable(),
    ut_met: z.boolean().nullable(),
    iimp_margin_ka: z.string().nullable(),
    iimp_met: z.boolean().nullable(),
    in_margin_ka: z.string().nullable(),
    in_met: z.boolean().nullable(),
    imax_margin_ka: z.string().nullable(),
    imax_met: z.boolean().nullable(),
    prospective_current_basis: prospectiveCurrentBasisSchema,
    prospective_current_ka: z.string().nullable(),
    isccr_margin_ka: z.string().nullable(),
    isccr_ratio: z.string().nullable(),
    isccr_met: z.boolean().nullable(),
    jurisdiction_profile: jurisdictionProfileSchema,
    items: z.array(protectedItemResponseSchema),
    status: surgeProtectionStatusSchema,
    warnings: z.array(surgeProtectionWarningSchema),
    notes: z.string().nullable(),
  })
  .strict();

export const surgeProtectionRunCreateRequestSchema = z
  .object({
    study: surgeProtectionRequestSchema,
    project_revision_id: z.string().uuid().optional(),
    notes: optionalRequestText(2000),
  })
  .strict();

export const surgeProtectionRunResponseSchema = z
  .object({
    run: calculationRunSummarySchema,
    result: surgeProtectionResponseSchema,
  })
  .strict();

export type DeclaredRequest = z.infer<typeof declaredRequestSchema>;
export type LeadsRequest = z.infer<typeof leadsRequestSchema>;
export type BackupDeviceRequest = z.infer<typeof backupDeviceRequestSchema>;
export type SpdRequest = z.infer<typeof spdRequestSchema>;
export type RequirementsRequest = z.infer<typeof requirementsRequestSchema>;
export type ProtectedItemRequest = z.infer<typeof protectedItemRequestSchema>;
export type SurgeProtectionRequest = z.infer<typeof surgeProtectionRequestSchema>;
export type SurgeProtectionWarning = z.infer<typeof surgeProtectionWarningSchema>;
export type DeclaredResponse = z.infer<typeof declaredResponseSchema>;
export type LinkedFaultRunResponse = z.infer<typeof linkedFaultRunResponseSchema>;
export type ProspectiveCurrentResponse = z.infer<typeof prospectiveCurrentResponseSchema>;
export type SpdResponse = z.infer<typeof spdResponseSchema>;
export type ProtectedItemResponse = z.infer<typeof protectedItemResponseSchema>;
export type SurgeProtectionResponse = z.infer<typeof surgeProtectionResponseSchema>;
export type SurgeProtectionRunCreateRequest = z.infer<typeof surgeProtectionRunCreateRequestSchema>;
export type SurgeProtectionRunResponse = z.infer<typeof surgeProtectionRunResponseSchema>;

export { ALWAYS_STUDY_INFORMATION_CODES };

function formatApiError(data: unknown, status: number, subject: string): string {
  return describeApiFieldError(data, status, `${subject} failed`);
}

function requestSignal(signal?: AbortSignal): AbortSignal {
  const timeoutSignal = AbortSignal.timeout(30_000);
  return signal ? AbortSignal.any([signal, timeoutSignal]) : timeoutSignal;
}

/**
 * Calculate a surge protection study and persist it as a run revision.
 *
 * With a project revision id the run is stored under that revision of the selected project -
 * and a linked fault run must then belong to the same revision; without one it belongs to no
 * project. An unchanged study returns the stored revision with HTTP 200 instead of a new one
 * (A11); both are valid here.
 */
export async function createSurgeProtectionRun(
  payload: SurgeProtectionRunCreateRequest,
  signal?: AbortSignal,
  projectRevisionId?: string,
): Promise<SurgeProtectionRunResponse> {
  const validatedPayload = surgeProtectionRunCreateRequestSchema.parse(payload);

  const response = await fetch(RUNS_URL, {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify(
      projectRevisionId === undefined
        ? validatedPayload
        : { ...validatedPayload, project_revision_id: projectRevisionId },
    ),
    cache: "no-store",
    signal: requestSignal(signal),
  });

  const data: unknown = await response.json().catch(() => null);

  if (!response.ok) {
    throw new ApiError(formatApiError(data, response.status, "Surge protection"), response.status);
  }

  const parsed = surgeProtectionRunResponseSchema.safeParse(data);

  if (!parsed.success) {
    throw new Error("Unexpected response from the KES Electrical OS surge protection runs API.");
  }

  return parsed.data;
}
